import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { fadeInOutAnimation } from '@app/core/animations';
import { AppState } from '@app/core/states/app.state';
import {
  Deeplink,
  EpisodeDeeplink,
  PodcastDeeplink,
  RadioDeeplink
} from '@app/features/notifications/models/notification.model';
import {
  FetchDeeplinksRequest,
  FetchEpisodeDeeplinksRequest,
  FetchPodcastDeeplinksRequest,
  FetchRadioDeeplinksRequest,
} from '@app/features/notifications/states/notifications.actions';
import { NotificationsState } from '@app/features/notifications/states/notifications.state';
import { DropdownOption } from '@app/ui/components/dropdown/dropdown.model';
import { Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'rk-notification-form-deeplinks',
  standalone: false,
  templateUrl: './notification-form-deeplinks.component.html',
  styleUrl: './notification-form-deeplinks.component.scss',
  animations: [fadeInOutAnimation],
})
export class NotificationFormDeeplinksComponent implements OnInit, OnDestroy, OnChanges {
  @Input() selectedDeeplink?: string;
  private readonly store = inject(Store);

  showDeeplinkOptions = false;

  tabOptions: DropdownOption[] = [];
  podcastOptions: DropdownOption[] = [];
  episodeOptions: DropdownOption[] = [];
  radioOptions: DropdownOption[] = [];

  selectedTab?: DropdownOption
  selectedPodcast?: PodcastDeeplink;
  selectedEpisode?: EpisodeDeeplink;
  selectedRadio?: RadioDeeplink;

  tabDeeplinks$: Observable<Deeplink[]> = this.store.select(NotificationsState.tabDeeplinks);
  podcastDeeplinks$: Observable<PodcastDeeplink[]> = this.store.select(NotificationsState.podcastDeeplinks);
  episodeDeeplinks$: Observable<EpisodeDeeplink[]> = this.store.select(NotificationsState.episodeDeeplinks);
  radioDeeplinks$: Observable<RadioDeeplink[]> = this.store.select(NotificationsState.radioDeeplinks);
  isDeeplinksLoading$: Observable<boolean> = this.store.select(NotificationsState.isDeeplinksLoading);
  deeplinkIsAvailable$: Observable<boolean> = this.store.select(
    AppState.deeplinkIsAvailable,
  );
  private readonly subscription = new Subscription();

  @Output() readonly deeplinkSelected = new EventEmitter<string | null>();

  ngOnInit(): void {
    const appId = this.store.selectSnapshot(AppState.currentAppId);
    if (appId) {
      this.store.dispatch(new FetchDeeplinksRequest(appId));
    }

    this.subscription.add(
      this.tabDeeplinks$.subscribe(links => {
        this.tabOptions = links.map(link => ({
          label: link.title,
          value: link.deeplink,
          faIcon: link.faIcon,
          legacyIcon: link.icon,
          type: link.type,
        }));
      }),
    );

    this.subscription.add(
      this.podcastDeeplinks$.subscribe(podcasts => {
        this.podcastOptions = podcasts.map(podcast => ({
          label: podcast.title,
          value: podcast.deeplink,
        }));
      }),
    );

    this.subscription.add(
      this.episodeDeeplinks$.subscribe(episodes => {
        this.episodeOptions = episodes.map(episode => ({
          label: episode.title,
          value: episode.deeplink,
        }));
      }),
    );

    this.subscription.add(
      this.radioDeeplinks$.subscribe(radios => {
        this.radioOptions = radios.map(radio => ({
          label: radio.title,
          value: radio.deeplink,
        }));

        this.selectedRadio = radios.find(radio => radio.deeplink === this.selectedDeeplink);

        // preselect the radio when the radio tab only contains one
        if (!this.selectedRadio && radios.length === 1 && this.isRadioDeeplinkSelected()) {
          this.selectRadio(radios[0]);
        }
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDeeplink']) {
      this.selectedRadio = this.store
        .selectSnapshot(NotificationsState.radioDeeplinks)
        .find(radio => radio.deeplink === this.selectedDeeplink);
    }
  }

  onTabSelected(deeplink: string) {

    this.deeplinkSelected.emit(deeplink);

    this.selectedTab = this.tabOptions.find(option => option.value === deeplink);

    // reset podcast, episode and radio selections
    this.selectedPodcast = undefined;
    this.podcastOptions = []
    this.selectedEpisode = undefined;
    this.episodeOptions = [];
    this.selectedRadio = undefined;
    this.radioOptions = [];

    // if the selected deeplink is of type 'podcasts', fetch podcast deeplinks
    if (this.selectedTab?.type === 'podcasts') {
      this.store.dispatch(new FetchPodcastDeeplinksRequest(deeplink));

    }

    // if the selected deeplink is of type 'radio', fetch radio deeplinks
    if (this.selectedTab?.type === 'radio') {
      this.store.dispatch(new FetchRadioDeeplinksRequest());
    }
  }

  onRadioSelected(radioDeeplink: string) {
    const selectedRadio = this.store.selectSnapshot(NotificationsState.radioDeeplinks)
      .find(radio => radio.deeplink === radioDeeplink);

    if (selectedRadio) {
      this.selectRadio(selectedRadio);
    }
  }

  private selectRadio(radio: RadioDeeplink) {
    this.selectedRadio = radio;
    this.deeplinkSelected.emit(radio.deeplink);
  }

  onPodcastSelected(podcastDeeplink: string) {
    const selectedPodcast = this.store.selectSnapshot(NotificationsState.podcastDeeplinks)
      .find(podcast => podcast.deeplink === podcastDeeplink);

    if (selectedPodcast) {
      this.selectedPodcast = selectedPodcast;
      // fetch episode deeplinks using the podcast deeplink and base64 rss feed
      this.store.dispatch(new FetchEpisodeDeeplinksRequest(selectedPodcast.deeplink, selectedPodcast.url));
      // reset episode selection
      this.selectedEpisode = undefined;
      this.episodeOptions = [];
      // emit the podcast deeplink as the selected deeplink
      this.deeplinkSelected.emit(podcastDeeplink);
    }
  }

  onEpisodeSelected(episodeDeeplink: string) {
    const selectedEpisode = this.store.selectSnapshot(NotificationsState.episodeDeeplinks)
      .find(episode => episode.deeplink === episodeDeeplink);

    if (selectedEpisode) {
      this.selectedEpisode = selectedEpisode;
      // emit the episode deeplink as the final selected deeplink
      this.deeplinkSelected.emit(episodeDeeplink);
    }
  }

  isPodcastDeeplinkSelected(): boolean {

    const isPodcast = this.selectedTab?.type === 'podcasts' || !!this.selectedPodcast;

    return isPodcast;
  }

  isRadioDeeplinkSelected(): boolean {
    return this.selectedTab?.type === 'radio';
  }

  getSelectedPodcastOption(): DropdownOption | undefined {
    if (!this.podcastOptions || this.podcastOptions.length === 0 || !this.selectedPodcast) {
      return undefined;
    }

    const foundOption = this.podcastOptions.find(option => option.value === this.selectedPodcast?.deeplink);

    return foundOption;
  }

  getSelectedEpisodeOption(): DropdownOption | undefined {
    if (!this.episodeOptions || this.episodeOptions.length === 0 || !this.selectedEpisode) {
      return undefined;
    }

    return this.episodeOptions.find(option => option.value === this.selectedEpisode?.deeplink);
  }

  getSelectedRadioOption(): DropdownOption | undefined {
    if (!this.radioOptions || this.radioOptions.length === 0 || !this.selectedRadio) {
      return undefined;
    }

    return this.radioOptions.find(option => option.value === this.selectedRadio?.deeplink);
  }

  toggleDeeplinkOption() {
    this.showDeeplinkOptions = !this.showDeeplinkOptions;

    if (!this.showDeeplinkOptions) {
      this.deeplinkSelected.emit(null);
      // reset podcast, episode and radio selections
      this.selectedTab = undefined
      this.selectedPodcast = undefined;
      this.selectedEpisode = undefined;
      this.selectedRadio = undefined;
    }
  }

  onResetPodcast(): void {
    // reset podcast and episode selections
    this.selectedPodcast = undefined;
    this.selectedEpisode = undefined;
    this.episodeOptions = [];

    // emit the tab deeplink as the selected deeplink (go back to tab level)
    if (this.selectedTab) {
      this.deeplinkSelected.emit(this.selectedTab.value);
    }
  }

  onResetEpisode(): void {
    // reset only episode selection
    this.selectedEpisode = undefined;

    // emit the podcast deeplink as the selected deeplink (go back to podcast level)
    if (this.selectedPodcast) {
      this.deeplinkSelected.emit(this.selectedPodcast.deeplink);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
